import { useState } from 'react';
import { Box, Typography, Chip, IconButton, Avatar } from '@mui/joy';
import { motion, AnimatePresence } from 'framer-motion';
import CreditCardRoundedIcon      from '@mui/icons-material/CreditCardRounded';
import PaymentsRoundedIcon        from '@mui/icons-material/PaymentsRounded';
import PointOfSaleRoundedIcon     from '@mui/icons-material/PointOfSaleRounded';
import ChatBubbleOutlineRounded   from '@mui/icons-material/ChatBubbleOutlineRounded';
import CheckRoundedIcon           from '@mui/icons-material/CheckRounded';
import DeleteOutlineRoundedIcon   from '@mui/icons-material/DeleteOutlineRounded';
import KeyboardArrowDownRounded   from '@mui/icons-material/KeyboardArrowDownRounded';

const MONEY_META = {
    transfer: { label: 'Перевод',  color: '#818cf8', bg: 'rgba(129,140,248,0.14)', icon: <CreditCardRoundedIcon  sx={{ fontSize: 13 }} /> },
    cash:     { label: 'Наличные', color: '#22c55e', bg: 'rgba(34,197,94,0.14)',   icon: <PaymentsRoundedIcon    sx={{ fontSize: 13 }} /> },
    terminal: { label: 'Терминал', color: '#f59e0b', bg: 'rgba(245,158,11,0.14)',  icon: <PointOfSaleRoundedIcon sx={{ fontSize: 13 }} /> },
};
const fmtTime     = (iso) => (iso ? new Date(iso).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }) : '—');
const fmtDateTime = (iso) => (iso ? new Date(iso).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—');
const fmtMoney    = (v)   => Number(v || 0).toLocaleString('ru-RU', { maximumFractionDigits: 0 });

/**
 * Компактная СТРОКА чека для рабочего аккаунта (история чеков + статистика).
 * Клик по строке — раскрывает детали (позиции, комментарий, оплаты, времена).
 * onConfirm(order) — подтверждение (для неподтверждённых).
 * onDelete(order)  — удаление/отклонение (родитель решает: заявка или отмена фантома).
 */
export default function CompactCheckCard({ group, onConfirm, onDelete, busy = false }) {
    const [expanded, setExpanded] = useState(false);
    if (!group || group.length === 0) return null;

    const head         = group[0];
    const groupTotal   = group.reduce((s, o) => s + parseFloat(o.value || 0), 0);
    const allItems     = group.flatMap(o => o.items || []);
    const groupComment = group.map(o => o.comment).find(c => c && c.trim()) || '';
    const isPending    = group.some(o => o.confirmation_status === 'pending');
    const isCancelled  = group.some(o => o.is_cancelled);
    const moneyTypes   = [...new Set(group.map(o => o.money_type))];
    const hasTwoTimes  = head.receipt_datetime
        && Math.abs(new Date(head.receipt_datetime).getTime() - new Date(head.date_create).getTime()) > 60_000;

    const stop = (fn) => (e) => { e.stopPropagation(); fn && fn(e); };

    return (
        <Box sx={{
            borderRadius: 'var(--joy-radius-md)',
            border: '1px solid var(--joy-palette-divider)',
            borderLeft: `3px solid ${isCancelled ? 'var(--joy-palette-neutral-500)' : isPending ? '#f59e0b' : '#22c55e'}`,
            bgcolor: 'var(--joy-palette-background-level1)',
            overflow: 'hidden',
            opacity: isCancelled ? 0.55 : 1,
        }}>
            {/* Верхняя строка — кликабельна, разворачивает детали */}
            <Box
                onClick={() => setExpanded(v => !v)}
                sx={{
                    display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.8,
                    cursor: 'pointer', userSelect: 'none',
                    transition: 'background .15s',
                    '&:hover': { bgcolor: 'var(--joy-palette-background-level2)' },
                }}
            >
                {/* Аватар */}
                <Avatar src={head.photographer_avatar_url} size="sm" sx={{ bgcolor: 'var(--joy-palette-primary-500)', flexShrink: 0 }}>
                    {(head.photographer_name || '').charAt(0).toUpperCase()}
                </Avatar>

                {/* Имя + точка·админ (одной строкой) + дата/время */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography sx={{ fontSize: 13.5, fontWeight: 700 }} noWrap>
                            {head.photographer_name}
                        </Typography>
                        {isCancelled && (
                            <Chip size="sm" variant="soft" color="danger"
                                sx={{ fontSize: 9, fontWeight: 700, '--Chip-minHeight': '18px' }}>
                                Отменён
                            </Chip>
                        )}
                        {groupComment && (
                            <ChatBubbleOutlineRounded sx={{ fontSize: 13, color: 'var(--joy-palette-text-tertiary)', flexShrink: 0 }} />
                        )}
                    </Box>
                    {(head.location_name || head.administrator_name) && (
                        <Typography sx={{ fontSize: 10.5, color: 'var(--joy-palette-text-tertiary)' }}>
                            {head.location_name}
                            {head.administrator_name && (
                                <> · Админ: <Box component="span" sx={{ color: '#06b6d4', fontWeight: 700 }}>{head.administrator_name}</Box></>
                            )}
                        </Typography>
                    )}
                    <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-tertiary)' }} noWrap>
                        {isPending
                            ? <Box component="span" sx={{ color: '#f59e0b', fontWeight: 700 }}>ожидает · {fmtDateTime(head.date_create)}</Box>
                            : fmtDateTime(head.date_create)}
                    </Typography>
                </Box>

                {/* Тип оплаты — колонка слева от суммы */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3, alignItems: 'flex-end', flexShrink: 0 }}>
                    {moneyTypes.map(mt => {
                        const meta = MONEY_META[mt] || MONEY_META.transfer;
                        return (
                            <Chip key={mt} size="sm" sx={{ bgcolor: meta.bg, color: meta.color, fontSize: 10, fontWeight: 700, '--Chip-minHeight': '20px' }}>
                                {meta.label}
                            </Chip>
                        );
                    })}
                </Box>

                {/* Сумма */}
                <Typography sx={{ fontSize: 15, fontWeight: 800, whiteSpace: 'nowrap', minWidth: 56, textAlign: 'right', flexShrink: 0 }}>
                    {fmtMoney(groupTotal)}
                </Typography>

                {/* Стрелка раскрытия */}
                <Box sx={{
                    flexShrink: 0, display: 'flex', alignItems: 'center', color: 'var(--joy-palette-text-tertiary)',
                    transition: 'transform .2s', transform: expanded ? 'rotate(180deg)' : 'none',
                }}>
                    <KeyboardArrowDownRounded sx={{ fontSize: 20 }} />
                </Box>

                {/* Подтвердить (для неподтверждённых) */}
                {isPending && onConfirm && (
                    <IconButton size="sm" variant="solid" color="success"
                        onClick={stop(() => group.forEach(o => o.confirmation_status === 'pending' && onConfirm(o)))}
                        disabled={busy}
                        sx={{ flexShrink: 0, borderRadius: 'var(--joy-radius-md)' }}>
                        <CheckRoundedIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                )}

                {/* Удалить */}
                {onDelete && (
                    <IconButton size="sm" variant="solid" color="danger"
                        onClick={stop(() => onDelete(head))} disabled={busy}
                        sx={{ flexShrink: 0, borderRadius: 'var(--joy-radius-md)' }}>
                        <DeleteOutlineRoundedIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                )}
            </Box>

            {/* Раскрываемые детали (анимация) */}
            <AnimatePresence initial={false}>
                {expanded && (
                    <motion.div
                        key="details"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.25, ease: 'easeInOut' }}
                        style={{ overflow: 'hidden' }}
                    >
                        <Box sx={{
                            px: 1, pt: 0.6, pb: 1,
                            borderTop: '1px dashed var(--joy-palette-divider)',
                            display: 'flex', flexDirection: 'column', gap: 0.9,
                        }}>
                            {/* Позиции — что было продано */}
                            <Box>
                                <Typography sx={{ fontSize: 9.5, fontWeight: 700, color: 'var(--joy-palette-text-tertiary)', textTransform: 'uppercase', letterSpacing: 0.4, mb: 0.4 }}>
                                    Позиции
                                </Typography>
                                {allItems.length > 0 ? (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                                        {allItems.map((it, i) => (
                                            <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                                                <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-secondary)' }}>
                                                    {it.product_name_snapshot}{it.quantity > 1 ? ` × ${it.quantity}` : ''}
                                                </Typography>
                                                {it.unit_price != null && (
                                                    <Typography sx={{ fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' }}>
                                                        {fmtMoney(it.unit_price * it.quantity)} ₽
                                                    </Typography>
                                                )}
                                            </Box>
                                        ))}
                                    </Box>
                                ) : (
                                    <Typography sx={{ fontSize: 11.5, color: 'var(--joy-palette-text-tertiary)' }}>
                                        Без товарных позиций (например, только электронные кадры)
                                    </Typography>
                                )}
                            </Box>

                            {/* Комментарий */}
                            {groupComment && (
                                <Box>
                                    <Typography sx={{ fontSize: 9.5, fontWeight: 700, color: 'var(--joy-palette-text-tertiary)', textTransform: 'uppercase', letterSpacing: 0.4, mb: 0.3 }}>
                                        Комментарий
                                    </Typography>
                                    <Box sx={{ px: 1, py: 0.6, borderRadius: 'var(--joy-radius-sm)', bgcolor: 'var(--joy-palette-background-level2)', borderLeft: '3px solid #f59e0b' }}>
                                        <Typography sx={{ fontSize: 11.5, color: 'var(--joy-palette-text-secondary)', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                            {groupComment}
                                        </Typography>
                                    </Box>
                                </Box>
                            )}

                            {/* Оплаты по чеку + времена + кто оформил */}
                            <Box>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.4, mb: 0.5 }}>
                                    {group.map(o => {
                                        const meta = MONEY_META[o.money_type] || MONEY_META.transfer;
                                        return (
                                            <Chip key={o.id} size="sm" startDecorator={meta.icon}
                                                sx={{ bgcolor: meta.bg, color: meta.color, fontSize: 9.5, fontWeight: 700, '--Chip-minHeight': '18px' }}>
                                                {meta.label} · {fmtMoney(o.value)} ₽
                                            </Chip>
                                        );
                                    })}
                                </Box>
                                <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-tertiary)' }}>
                                    {head.processed_by_name && (
                                        <>Оформил: <Box component="span" sx={{ color: '#22c55e', fontWeight: 700 }}>{head.processed_by_name}</Box> · </>
                                    )}
                                    {hasTwoTimes
                                        ? <>фото {fmtDateTime(head.receipt_datetime)} → админ {fmtTime(head.date_create)}</>
                                        : <>{fmtDateTime(head.date_create)}</>}
                                </Typography>
                            </Box>
                        </Box>
                    </motion.div>
                )}
            </AnimatePresence>
        </Box>
    );
}